import {
  getI18nextCacheTtlMs,
  isI18nextCacheEnabled,
} from './apiCacheConfig';

const DB_NAME = 'i18next_translations';
const STORE_NAME = 'translations';
/** Legacy store from src/i18nextIdbBackend.js — removed on upgrade. */
const LEGACY_STORE_NAME = 'resources';
const DB_VERSION = 2;

export { I18NEXT_TRANSLATION_TTL_MS } from './apiCacheConfig';

function getTtlMs() {
  return getI18nextCacheTtlMs();
}

const LS_PREFIX = 'i18n_tx_';

let _dbPromise = null;
const memoryCache = new Map();
const pendingFetches = new Map();

export function normalizeLanguage(lng) {
  return (lng || 'en').split('-')[0].toLowerCase();
}

export function getI18nextLoadPath() {
  const path = window._env_?.I18NEXT_API_PATH;
  if (!path || path === 'undefined') return '';
  return String(path).replace('qat', 'stage').replace('dev', 'stage');
}

export function buildTranslationUrl(lng, ns) {
  const template = getI18nextLoadPath();
  if (!template) return '';
  return template
    .replace(/\{\{lng\}\}/g, normalizeLanguage(lng))
    .replace(/\{\{ns\}\}/g, ns);
}

export function buildTranslationCacheKey(url) {
  const withoutQuery = (url || '').split('?')[0].replace(/\/$/, '');
  const match = withoutQuery.match(
    /\/translations\/([a-z]{2}(?:-[A-Z]{2})?)\/([^/]+)$/i
  ) || withoutQuery.match(
    /\/([a-z]{2}(?:-[A-Z]{2})?)\/([^/]+)\.json$/i
  ) || withoutQuery.match(
    /\/([a-z]{2}(?:-[A-Z]{2})?)\/([^/]+)$/i
  );
  if (match) {
    return `${normalizeLanguage(match[1])}__${match[2]}`;
  }
  return `url__${withoutQuery}`;
}

export function buildTranslationCacheKeyFromParts(lng, ns) {
  return `${normalizeLanguage(lng)}__${ns}`;
}

function isValidTranslationPayload(text) {
  if (!text || typeof text !== 'string') return false;
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
  } catch {
    return false;
  }
}

function normalizeCachedData(data) {
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    try {
      return JSON.stringify(data);
    } catch {
      return null;
    }
  }
  return null;
}

function readStorageEntry(storage, cacheKey) {
  try {
    const raw = storage.getItem(LS_PREFIX + cacheKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (entry.expiresAt && Date.now() > entry.expiresAt) {
      storage.removeItem(LS_PREFIX + cacheKey);
      return null;
    }
    return normalizeCachedData(entry.data);
  } catch {
    return null;
  }
}

function writeStorageEntry(storage, cacheKey, data) {
  if (!isI18nextCacheEnabled()) return;
  const ttlMs = getTtlMs();
  if (!ttlMs) return;
  try {
    storage.setItem(LS_PREFIX + cacheKey, JSON.stringify({
      data,
      expiresAt: Date.now() + ttlMs,
    }));
  } catch {
    /* quota exceeded — IDB remains source of truth */
  }
}

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
      }
      // v1 used store "resources" (i18nextIdbBackend.js); drop it after creating "translations"
      if (db.objectStoreNames.contains(LEGACY_STORE_NAME)) {
        db.deleteObjectStore(LEGACY_STORE_NAME);
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => {
      console.error('[i18nextIDBCache] Failed to open IndexedDB:', req.error?.name, req.error?.message);
      _dbPromise = null;
      reject(req.error);
    };
    req.onblocked = () => {
      console.warn('[i18nextIDBCache] IndexedDB upgrade blocked — close other tabs with this app open');
    };
  });
  return _dbPromise;
}

async function idbGet(cacheKey) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .get(cacheKey);
      req.onsuccess = () => {
        const record = req.result;
        if (!record) return resolve(null);
        if (record.expiresAt && Date.now() > record.expiresAt) return resolve(null);
        resolve(normalizeCachedData(record.data));
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function getCachedTranslation(cacheKey, url) {
  if (!isI18nextCacheEnabled()) return null;

  if (memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey);
  }

  const fromSession = readStorageEntry(sessionStorage, cacheKey);
  if (fromSession) {
    memoryCache.set(cacheKey, fromSession);
    return fromSession;
  }

  const fromLocal = readStorageEntry(localStorage, cacheKey);
  if (fromLocal) {
    memoryCache.set(cacheKey, fromLocal);
    return fromLocal;
  }

  const fromIdb = await idbGetWithFallback(cacheKey, url);
  if (fromIdb) {
    memoryCache.set(cacheKey, fromIdb);
    writeStorageEntry(sessionStorage, cacheKey, fromIdb);
    writeStorageEntry(localStorage, cacheKey, fromIdb);
    return fromIdb;
  }

  return null;
}

async function idbGetWithFallback(cacheKey, url) {
  const primary = await idbGet(cacheKey);
  if (primary) return primary;

  const legacyKey = `url__${(url || '').split('?')[0].replace(/\/$/, '')}`;
  if (legacyKey !== cacheKey) {
    return idbGet(legacyKey);
  }
  return null;
}

async function persistTranslation(cacheKey, data) {
  if (!isI18nextCacheEnabled()) return;
  const ttlMs = getTtlMs();
  if (!ttlMs) return;

  const normalized = normalizeCachedData(data);
  if (!isValidTranslationPayload(normalized)) return;

  memoryCache.set(cacheKey, normalized);
  writeStorageEntry(sessionStorage, cacheKey, normalized);
  writeStorageEntry(localStorage, cacheKey, normalized);

  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({
        cacheKey,
        data: normalized,
        expiresAt: Date.now() + ttlMs,
      });
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.error('[i18nextIDBCache] IndexedDB write failed:', e);
  }
}

export async function preloadResourcesFromIDB(lng, namespaces) {
  if (!isI18nextCacheEnabled()) return {};

  const normalizedLng = normalizeLanguage(lng);
  const resources = { [normalizedLng]: {} };

  await Promise.all(
    namespaces.map(async (ns) => {
      const cacheKey = buildTranslationCacheKeyFromParts(normalizedLng, ns);
      const url = buildTranslationUrl(normalizedLng, ns);
      const cached = await getCachedTranslation(cacheKey, url);
      if (!cached) return;
      try {
        resources[normalizedLng][ns] = JSON.parse(cached);
      } catch {
        /* skip invalid cache row */
      }
    })
  );

  if (Object.keys(resources[normalizedLng]).length === 0) return {};
  return resources;
}

export function areAllNamespacesCached(lng, namespaces, resources) {
  const normalizedLng = normalizeLanguage(lng);
  return namespaces.every((ns) => Boolean(resources[normalizedLng]?.[ns]));
}

export async function clearAllI18nextTranslations() {
  memoryCache.clear();

  [sessionStorage, localStorage].forEach((storage) => {
    try {
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (key && key.startsWith(LS_PREFIX)) {
          storage.removeItem(key);
        }
      }
    } catch {
      /* ignore */
    }
  });

  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
  } catch (e) {
    console.error('[i18nextIDBCache] clearAll failed:', e);
  }
}

export async function pruneExpiredI18nextTranslations() {
  const now = Date.now();

  [sessionStorage, localStorage].forEach((storage) => {
    try {
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (!key || !key.startsWith(LS_PREFIX)) continue;
        try {
          const entry = JSON.parse(storage.getItem(key));
          if (entry.expiresAt && now > entry.expiresAt) {
            storage.removeItem(key);
          }
        } catch {
          storage.removeItem(key);
        }
      }
    } catch {
      /* ignore */
    }
  });

  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.openCursor();
      req.onsuccess = (e) => {
        const cursor = e.target.result;
        if (!cursor) return;
        if (cursor.value.expiresAt && now > cursor.value.expiresAt) {
          memoryCache.delete(cursor.value.cacheKey);
          cursor.delete();
        }
        cursor.continue();
      };
      req.onerror = () => {
        console.error('[i18nextIDBCache] Prune cursor error:', req.error?.name, req.error?.message);
      };
      tx.oncomplete = resolve;
      tx.onerror = () => {
        console.error('[i18nextIDBCache] Prune transaction error:', tx.error?.name, tx.error?.message);
        resolve();
      };
    });
  } catch (e) {
    console.error('[i18nextIDBCache] pruneExpired failed:', e?.name, e?.message || e);
  }
}

export async function loadTranslationViaIDB(_options, url) {
  const cacheKey = buildTranslationCacheKey(url);
  const cacheEnabled = isI18nextCacheEnabled();

  if (cacheEnabled) {
    const cached = await getCachedTranslation(cacheKey, url);
    if (cached) return cached;
  }

  if (pendingFetches.has(cacheKey)) {
    return pendingFetches.get(cacheKey);
  }

  const fetchPromise = (async () => {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'same-origin',
    });
    const data = await response.text();

    if (!response.ok) {
      throw new Error(`i18next load failed: ${response.status} ${url}`);
    }

    if (cacheEnabled) {
      await persistTranslation(cacheKey, data);
    }
    return normalizeCachedData(data);
  })();

  pendingFetches.set(cacheKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    pendingFetches.delete(cacheKey);
  }
}
